import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft, Printer, FilePdf, Plus, Trash, Warning, PencilSimple, EnvelopeSimple, Truck, Eye } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [business, setBusiness] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ to_email: "", subject: "", message: "" });
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_date: new Date().toISOString().split("T")[0],
    method: "cash",
    notes: "",
  });

  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfOptions, setPdfOptions] = useState({ show_bank: false, logo_align: "left" });

  // Inline PDF preview (object URL is created on demand and revoked on close)
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const [ewbStatus, setEwbStatus] = useState(null);
  const [ewbDialogOpen, setEwbDialogOpen] = useState(false);
  const [ewbForm, setEwbForm] = useState({ transporter_id: "", transporter_name: "", vehicle_number: "", mode_of_transport: "1", distance_km: 0 });
  const [ewbError, setEwbError] = useState("");
  const [ewbSms, setEwbSms] = useState(null);
  const [ewbSmsLoading, setEwbSmsLoading] = useState(false);
  const [ewbSmsCopied, setEwbSmsCopied] = useState(false);
  const [ewbJsonLoading, setEwbJsonLoading] = useState(false);
  const [ewbNumberInput, setEwbNumberInput] = useState("");
  const [ewbSaving, setEwbSaving] = useState(false);
  const [ewbAutoLoading, setEwbAutoLoading] = useState(false);

  // E-Invoice (IRN) via master GSP
  const [einvStatus, setEinvStatus] = useState(null);
  const [einvGenerating, setEinvGenerating] = useState(false);
  const [einvError, setEinvError] = useState("");

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchEwbStatus = async () => {
    try {
      const res = await axios.get(`${API}/invoices/${id}/ewb/status`);
      setEwbStatus(res.data);
    } catch (err) {
      console.error("EWB status fetch failed:", err);
    }
  };

  const fetchEinvStatus = async () => {
    try {
      const res = await axios.get(`${API}/invoices/${id}/einvoice/status`);
      setEinvStatus(res.data);
    } catch (err) {
      console.error("E-Invoice status fetch failed:", err);
    }
  };

  useEffect(() => {
    if (invoice) { fetchEwbStatus(); fetchEinvStatus(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice]);

  const handleAutoGenerateEwb = async () => {
    if (!window.confirm("Auto-generate via Master GSP? ₹1.00 will be deducted from your wallet and the EWB number will be generated.")) return;
    setEwbAutoLoading(true); setEwbError("");
    try {
      await axios.post(`${API}/invoices/${id}/ewb/generate`, {
        transporter_id: ewbForm.transporter_id || null,
        transporter_name: ewbForm.transporter_name || null,
        vehicle_number: ewbForm.vehicle_number || null,
        mode_of_transport: ewbForm.mode_of_transport || "1",
        distance_km: parseInt(ewbForm.distance_km || 0, 10) || 0,
      });
      setEwbDialogOpen(false);
      await fetchData();
      await fetchEwbStatus();
    } catch (err) {
      setEwbError(err.response?.data?.detail || "Auto-generation failed");
    } finally { setEwbAutoLoading(false); }
  };

  const handleGenerateEinvoice = async () => {
    if (!window.confirm("Generate E-Invoice (IRN)? ₹1.00 will be deducted from your wallet.")) return;
    setEinvGenerating(true); setEinvError("");
    try {
      await axios.post(`${API}/invoices/${id}/einvoice/generate`, {});
      await fetchData();
      await fetchEinvStatus();
    } catch (err) {
      setEinvError(err.response?.data?.detail || "E-Invoice generation failed");
      alert(err.response?.data?.detail || "E-Invoice generation failed");
    } finally { setEinvGenerating(false); }
  };

  const openEwbDialog = () => {
    setEwbError("");
    setEwbSms(null);
    setEwbSmsCopied(false);
    setEwbNumberInput("");
    setEwbForm({
      transporter_id: "",
      transporter_name: "",
      vehicle_number: invoice?.vehicle_no || "",
      mode_of_transport: "1",
      distance_km: 0,
    });
    setEwbDialogOpen(true);
  };

  const handleFetchSms = async () => {
    setEwbSmsLoading(true);
    setEwbError("");
    try {
      const res = await axios.get(`${API}/invoices/${id}/ewb/sms-text`, {
        params: { distance: parseInt(ewbForm.distance_km || 0, 10), vehicle: ewbForm.vehicle_number || "" },
      });
      setEwbSms(res.data);
    } catch (err) {
      setEwbError(err.response?.data?.detail || "Failed to generate SMS text");
    } finally {
      setEwbSmsLoading(false);
    }
  };

  const handleCopySms = async () => {
    if (!ewbSms?.sms_text) return;
    try {
      await navigator.clipboard.writeText(ewbSms.sms_text);
      setEwbSmsCopied(true);
      setTimeout(() => setEwbSmsCopied(false), 2000);
    } catch (e) { /* clipboard blocked */ }
  };

  const handleDownloadEwbJson = async () => {
    setEwbJsonLoading(true);
    setEwbError("");
    try {
      const res = await axios.post(`${API}/ewb/bulk-json`, {
        invoice_ids: [id],
        distance_km: parseInt(ewbForm.distance_km || 0, 10),
        vehicle_no: ewbForm.vehicle_number || "",
        transporter_id: ewbForm.transporter_id || "",
        transporter_name: ewbForm.transporter_name || "",
        mode_of_transport: ewbForm.mode_of_transport,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `eway_${(invoice?.invoice_no || "bill").replace(/\//g, "_")}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setEwbError("JSON download failed");
    } finally {
      setEwbJsonLoading(false);
    }
  };

  const handleSaveEwbNumber = async () => {
    setEwbSaving(true);
    setEwbError("");
    try {
      await axios.post(`${API}/invoices/${id}/ewb/record`, {
        ewb_number: ewbNumberInput,
        vehicle_number: ewbForm.vehicle_number || "",
        distance_km: parseInt(ewbForm.distance_km || 0, 10),
        transporter_id: ewbForm.transporter_id || "",
        transporter_name: ewbForm.transporter_name || "",
        mode_of_transport: ewbForm.mode_of_transport,
      });
      setEwbDialogOpen(false);
      await fetchData();
      await fetchEwbStatus();
    } catch (err) {
      setEwbError(err.response?.data?.detail || "Failed to save the EWB number");
    } finally {
      setEwbSaving(false);
    }
  };

  const fetchData = async () => {
    try {
      const [invoiceRes, settingsRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/invoices/${id}`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/invoices/${id}/payments`),
      ]);
      setInvoice(invoiceRes.data);
      setBusiness(settingsRes.data);
      setPayments(paymentsRes.data);
      setPdfOptions(prev => ({
        ...prev,
        logo_align: settingsRes.data?.logo_alignment || "left",
        // Default the "Show Bank Account Details" checkbox from the saved global setting
        // (Settings → Show Bank Details on PDF). User can still un-tick per-download.
        show_bank: settingsRes.data?.show_bank_details !== false,
      }));
    } catch (error) {
      console.error("Error fetching invoice:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const handleDownloadPdf = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${id}/pdf?show_bank=${pdfOptions.show_bank}&logo_align=${pdfOptions.logo_align}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${invoice.invoice_no}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setPdfDialogOpen(false);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Failed to download PDF");
    }
  };

  // Build a one-off blob URL for the rendered PDF and show it inside an iframe.
  // Reuses the same backend route used by Download so the preview is 1:1 with
  // the file the user will receive. The blob URL is revoked when the dialog
  // closes to avoid leaking memory across repeated previews.
  const handlePreviewPdf = async () => {
    setPreviewError("");
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const response = await axios.get(
        `${API}/invoices/${id}/pdf?show_bank=${pdfOptions.show_bank}&logo_align=${pdfOptions.logo_align}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      setPreviewUrl(url);
    } catch (error) {
      console.error("Error previewing PDF:", error);
      setPreviewError("Failed to load preview. Please try again.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl("");
    }
    setPreviewError("");
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/invoices/${id}/payments`, {
        ...paymentForm,
        amount: parseFloat(paymentForm.amount),
      });
      setPaymentDialogOpen(false);
      setPaymentForm({ amount: "", payment_date: new Date().toISOString().split("T")[0], method: "cash", notes: "" });
      fetchData();
    } catch (error) {
      console.error("Error adding payment:", error);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm("Delete this payment?")) return;
    try {
      await axios.delete(`${API}/payments/${paymentId}`);
      fetchData();
    } catch (error) {
      console.error("Error deleting payment:", error);
    }
  };

  const handleEmailInvoice = async (e) => {
    e.preventDefault();
    setEmailSending(true);
    setEmailResult(null);
    try {
      const res = await axios.post(`${API}/invoices/${id}/email`, emailForm);
      setEmailResult(res.data);
      setTimeout(() => { setEmailDialogOpen(false); setEmailResult(null); }, 3000);
    } catch (error) {
      setEmailResult({ status: "error", message: error.response?.data?.detail || "Failed to send" });
    } finally {
      setEmailSending(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-96 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">Invoice not found</h2>
        <button onClick={() => navigate("/invoices")} className="px-6 py-3 bg-blue-600 text-white rounded-sm font-semibold">Back to Invoices</button>
      </div>
    );
  }

  const hasBusinessDetails = business && business.business_name;
  const balanceDue = invoice.total - (invoice.amount_paid || 0);
  const isOverdue = invoice.due_date && invoice.status !== "paid" && new Date(invoice.due_date) < new Date();

  return (
    <div data-testid="invoice-detail-page">
      {/* Action Bar */}
      <div className="no-print bg-white border-b border-slate-200 p-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate("/invoices")} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors duration-200">
            <ArrowLeft size={20} />
            <span className="text-sm font-semibold">Back to Invoices</span>
          </button>
          <div className="flex gap-3 flex-wrap">
            {!ewbStatus?.already_generated && (
              <button
                onClick={openEwbDialog}
                data-testid="ewb-generate-btn"
                className={`px-4 py-2 rounded-sm text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${ewbStatus?.required ? "bg-amber-600 text-white hover:bg-amber-700" : "bg-white border border-amber-300 text-amber-700 hover:bg-amber-50"}`}
                title={ewbStatus?.required ? `Required: ${ewbStatus.type}-state invoice of Rs.${ewbStatus.total?.toFixed(2)} exceeds threshold Rs.${ewbStatus.threshold?.toLocaleString()}` : "Generate E-Way Bill (free NIC methods)"}
              >
                <Truck size={20} weight="bold" /> E-Way Bill
              </button>
            )}
            {ewbStatus?.already_generated && (
              <div
                data-testid="ewb-generated-badge"
                className="px-4 py-2 bg-emerald-50 border border-emerald-300 text-emerald-800 rounded-sm text-sm font-semibold flex items-center gap-2"
                title={`Valid until: ${ewbStatus.record?.valid_until || "NIC auto"}`}
              >
                <Truck size={18} weight="duotone" />
                EWB: {ewbStatus.record?.ewb_number}
              </div>
            )}
            {!einvStatus?.already_generated && (
              <button
                onClick={handleGenerateEinvoice}
                data-testid="einvoice-generate-btn"
                disabled={einvGenerating}
                title={einvStatus?.configured ? "Generate E-Invoice IRN via platform GSP (₹1.00 wallet)" : "Configure Portal API user in Settings + Super Admin GSP first"}
                className="px-4 py-2 bg-blue-700 text-white rounded-sm text-sm font-semibold hover:bg-blue-800 transition-all duration-200 flex items-center gap-2 disabled:opacity-60"
              >
                <FilePdf size={18} weight="bold" /> {einvGenerating ? "Generating…" : "E-Invoice"}
              </button>
            )}
            {einvStatus?.already_generated && (
              <div
                data-testid="einvoice-generated-badge"
                className="px-4 py-2 bg-blue-50 border border-blue-300 text-blue-800 rounded-sm text-sm font-semibold flex items-center gap-2"
                title={`IRN: ${einvStatus.record?.irn}`}
              >
                IRN: {einvStatus.record?.irn?.slice(0, 12)}…
              </div>
            )}
            <button onClick={() => { setEmailForm({ to_email: "", subject: `Invoice ${invoice.invoice_no}`, message: "" }); setEmailDialogOpen(true); }} data-testid="email-invoice-btn" className="px-4 py-2 bg-green-600 text-white rounded-sm text-sm font-semibold hover:bg-green-700 transition-all duration-200 flex items-center gap-2">
              <EnvelopeSimple size={20} weight="bold" /> Email
            </button>
            <button onClick={() => navigate(`/invoices/${id}/edit`)} data-testid="edit-invoice-btn" className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-sm text-sm font-semibold hover:bg-slate-50 transition-all duration-200 flex items-center gap-2">
              <PencilSimple size={20} weight="bold" /> Edit
            </button>
            <button onClick={() => navigate(`/notes/from-invoice/${id}?type=credit`)} data-testid="create-credit-note-btn" className="px-4 py-2 bg-white border border-red-200 text-red-700 rounded-sm text-sm font-semibold hover:bg-red-50 transition-all duration-200">
              + Credit Note
            </button>
            <button onClick={() => navigate(`/notes/from-invoice/${id}?type=debit`)} data-testid="create-debit-note-btn" className="px-4 py-2 bg-white border border-emerald-200 text-emerald-700 rounded-sm text-sm font-semibold hover:bg-emerald-50 transition-all duration-200">
              + Debit Note
            </button>
            <button onClick={handlePreviewPdf} data-testid="preview-pdf-btn" className="px-4 py-2 bg-white border border-slate-300 text-slate-800 rounded-sm text-sm font-semibold hover:bg-slate-50 transition-all duration-200 flex items-center gap-2">
              <Eye size={20} weight="bold" /> Preview
            </button>
            <button onClick={() => setPdfDialogOpen(true)} data-testid="download-pdf-btn" className="px-4 py-2 bg-slate-900 text-white rounded-sm text-sm font-semibold hover:bg-slate-800 transition-all duration-200 flex items-center gap-2">
              <FilePdf size={20} weight="bold" /> Download PDF
            </button>
            <button onClick={handlePrint} data-testid="print-invoice-btn" className="px-4 py-2 bg-blue-600 text-white rounded-sm text-sm font-semibold hover:bg-blue-700 transition-all duration-200 flex items-center gap-2">
              <Printer size={20} weight="bold" /> Print
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-8 bg-slate-50">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Invoice Card */}
          <div className="bg-white border border-slate-200 rounded-sm print-container">
            <div className="p-12">
              {/* Header */}
              <div className="flex justify-between items-start mb-12 pb-8 border-b-2 border-slate-900">
                <div className="flex items-center gap-4">
                  {business?.logo && (
                    <img src={business.logo} alt="Logo" className="w-16 h-16 object-contain" data-testid="invoice-company-logo" />
                  )}
                  <div>
                    <h1 className="text-5xl font-black tracking-tighter text-slate-900 mb-2">INVOICE</h1>
                    <p className="text-sm text-slate-600">{invoice.invoice_no}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Invoice Date</p>
                  <p className="text-lg font-semibold text-slate-900">{format(new Date(invoice.invoice_date), "dd MMMM yyyy")}</p>
                  {invoice.due_date && (
                    <div className="mt-2">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Due Date</p>
                      <p className={`text-sm font-semibold ${isOverdue ? "text-red-600" : "text-slate-900"}`}>
                        {format(new Date(invoice.due_date), "dd MMMM yyyy")}
                        {isOverdue && <Warning size={14} weight="fill" className="inline ml-1" />}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* From / To */}
              <div className="grid grid-cols-2 gap-12 mb-12">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">From</p>
                  <div className="text-sm space-y-1">
                    {hasBusinessDetails ? (
                      <>
                        <p className="text-lg font-bold text-slate-900">{business.business_name}</p>
                        <p className="text-slate-600">{business.address}</p>
                        <p className="text-slate-600">{business.city}, {business.state} - {business.pincode}</p>
                        <p className="text-slate-600 font-mono">GSTIN: {business.gstin}</p>
                        {business.phone && <p className="text-slate-600">Phone: {business.phone}</p>}
                        {business.email && <p className="text-slate-600">Email: {business.email}</p>}
                      </>
                    ) : (
                      <>
                        <p className="text-lg font-bold text-amber-600">Business details not configured</p>
                        <p className="text-sm text-slate-500">Go to Settings to add your business details</p>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Bill To</p>
                  <div className="text-sm space-y-1">
                    <p className="text-lg font-bold text-slate-900">{invoice.customer_name}</p>
                    <p className="text-slate-600">{invoice.customer_address}</p>
                    <p className="text-slate-600">{invoice.customer_state} ({invoice.customer_state_code})</p>
                    {invoice.customer_gstin && <p className="text-slate-600 font-mono">GSTIN: {invoice.customer_gstin}</p>}
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <div className="mb-12">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-slate-900">
                      <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Description</th>
                      <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-slate-600">HSN/SAC</th>
                      <th className="text-right py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Qty</th>
                      <th className="text-right py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Rate</th>
                      <th className="text-right py-3 text-xs font-bold uppercase tracking-wider text-slate-600">GST %</th>
                      <th className="text-right py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.map((item, index) => (
                      <tr key={index} className="border-b border-slate-200">
                        <td className="py-4 text-sm font-semibold text-slate-900">{item.product_name}</td>
                        <td className="py-4 text-sm font-mono text-slate-600">{item.hsn_sac}</td>
                        <td className="py-4 text-sm text-slate-600 text-right">{item.quantity}</td>
                        <td className="py-4 text-sm text-slate-600 text-right">{"\u20b9"}{item.price.toFixed(2)}</td>
                        <td className="py-4 text-sm text-slate-600 text-right">{item.gst_rate}%</td>
                        <td className="py-4 text-sm font-semibold text-slate-900 text-right">{"\u20b9"}{item.amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="flex justify-end mb-12">
                <div className="w-80 space-y-3">
                  {invoice.rcm && (
                    <div className="p-2 bg-red-50 border border-red-200 rounded-sm text-xs font-bold text-red-700 uppercase tracking-wider" data-testid="invoice-rcm-banner">
                      Reverse Charge Mechanism (RCM) — Tax payable by recipient
                    </div>
                  )}
                  <div className="flex justify-between text-sm border-b border-slate-200 pb-3">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="font-semibold text-slate-900">{"\u20b9"}{invoice.subtotal.toFixed(2)}</span>
                  </div>
                  {invoice.cgst > 0 && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">CGST</span>
                        <span className="font-semibold text-slate-900">{"\u20b9"}{invoice.cgst.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">SGST</span>
                        <span className="font-semibold text-slate-900">{"\u20b9"}{invoice.sgst.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  {invoice.igst > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">IGST</span>
                      <span className="font-semibold text-slate-900">{"\u20b9"}{invoice.igst.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-3 border-t-2 border-slate-900">
                    <span className="text-lg font-bold text-slate-900">{invoice.rcm ? "Total (Taxable)" : "Total"}</span>
                    <span className="text-2xl font-black text-slate-900">{"\u20b9"}{invoice.total.toFixed(2)}</span>
                  </div>
                  {(invoice.amount_paid || 0) > 0 && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-green-600 font-semibold">Paid</span>
                        <span className="font-semibold text-green-600">{"\u20b9"}{invoice.amount_paid.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-slate-200">
                        <span className="font-bold text-slate-900">Balance Due</span>
                        <span className={`text-xl font-black ${balanceDue > 0 ? "text-red-600" : "text-green-600"}`}>{"\u20b9"}{balanceDue.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {invoice.notes && (
                <div className="mb-12">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Notes</p>
                  <p className="text-sm text-slate-600 whitespace-pre-line">{invoice.notes}</p>
                </div>
              )}

              {invoice.rcm && (
                <div className="mb-12" data-testid="invoice-rcm-note">
                  <p className="text-xs font-bold uppercase tracking-wider text-red-600 mb-3">Reverse Charge Declaration</p>
                  <p className="text-sm text-slate-600">This invoice is under the Reverse Charge Mechanism (RCM), and the responsibility to pay the tax lies with the buyer or recipient.</p>
                </div>
              )}

              <div className="flex justify-between items-center">
                <span className={`inline-flex px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-wider ${
                  invoice.status === "paid" ? "bg-green-100 text-green-800" : invoice.status === "partial" ? "bg-blue-100 text-blue-800" : isOverdue ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"
                }`}>
                  {isOverdue && invoice.status !== "paid" ? "OVERDUE" : invoice.status}
                </span>
                <p className="text-xs text-slate-400">Thank you for your business!</p>
              </div>
            </div>
          </div>

          {/* Payment Tracking */}
          <div className="bg-white border border-slate-200 rounded-sm no-print">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Payment History</h2>
                <p className="text-sm text-slate-500 mt-1">Track payments for this invoice</p>
              </div>
              {invoice.status !== "paid" && (
                <button onClick={() => setPaymentDialogOpen(true)} data-testid="add-payment-btn" className="px-3 py-2 bg-green-600 text-white rounded-sm text-sm font-semibold hover:bg-green-700 transition-all duration-200 flex items-center gap-2">
                  <Plus size={16} weight="bold" /> Record Payment
                </button>
              )}
            </div>

            {payments.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-slate-500">No payments recorded yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Method</th>
                      <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Notes</th>
                      <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {payments.map((payment) => (
                      <tr key={payment.id} data-testid={`payment-row-${payment.id}`} className="hover:bg-slate-50">
                        <td className="px-6 py-4 text-sm text-slate-900">{format(new Date(payment.payment_date), "dd MMM yyyy")}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-green-700">{"\u20b9"}{payment.amount.toFixed(2)}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 capitalize">{payment.method}</td>
                        <td className="px-6 py-4 text-sm text-slate-500">{payment.notes || "--"}</td>
                        <td className="px-6 py-4 text-right">
                          <button onClick={() => handleDeletePayment(payment.id)} data-testid={`delete-payment-${payment.id}`} className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-sm transition-all duration-200">
                            <Trash size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Record Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddPayment} className="space-y-4">
            <div>
              <Label>Amount *</Label>
              <Input type="number" step="0.01" min="0.01" data-testid="payment-amount-input" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} placeholder={`Balance: \u20b9${balanceDue.toFixed(2)}`} required />
            </div>
            <div>
              <Label>Payment Date *</Label>
              <Input type="date" data-testid="payment-date-input" value={paymentForm.payment_date} onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} required />
            </div>
            <div>
              <Label>Method</Label>
              <select data-testid="payment-method-select" value={paymentForm.method} onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="upi">UPI</option>
                <option value="cheque">Cheque</option>
                <option value="card">Card</option>
              </select>
            </div>
            <div>
              <Label>Notes</Label>
              <Input data-testid="payment-notes-input" value={paymentForm.notes} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} placeholder="Optional note" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-payment-btn" className="bg-green-600 hover:bg-green-700">Save Payment</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>


      {/* PDF Options Dialog */}
      <Dialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">PDF Options</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Logo Alignment</Label>
              <div className="flex gap-3">
                {["left", "right"].map((a) => (
                  <label key={a} className={`flex items-center gap-2 px-4 py-2 border rounded-sm cursor-pointer transition-all ${pdfOptions.logo_align === a ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}>
                    <input type="radio" name="pdf_logo_align" value={a} checked={pdfOptions.logo_align === a} onChange={() => setPdfOptions({...pdfOptions, logo_align: a})} className="accent-blue-600" />
                    <span className="text-sm font-semibold capitalize">{a}</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" data-testid="pdf-show-bank" checked={pdfOptions.show_bank} onChange={(e) => setPdfOptions({...pdfOptions, show_bank: e.target.checked})} className="w-4 h-4 accent-blue-600 rounded" />
              <span className="text-sm font-semibold text-slate-900">Show Bank Account Details</span>
            </label>
          </div>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setPdfDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDownloadPdf} data-testid="confirm-download-pdf" className="bg-slate-900 hover:bg-slate-800">Download PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline PDF Preview Dialog — renders the same PDF the user would
          download inside an iframe so they can sanity-check layout / theme /
          totals before grabbing the file. */}
      <Dialog open={previewOpen} onOpenChange={(o) => (o ? setPreviewOpen(true) : closePreview())}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] p-0 flex flex-col">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-200">
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Eye size={22} weight="duotone" /> Invoice Preview — {invoice?.invoice_no}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 bg-slate-100 no-dark overflow-hidden">
            {previewLoading && (
              <div className="h-full flex items-center justify-center text-slate-600 text-sm">
                Rendering preview…
              </div>
            )}
            {!previewLoading && previewError && (
              <div className="h-full flex items-center justify-center text-red-600 text-sm">
                {previewError}
              </div>
            )}
            {!previewLoading && !previewError && previewUrl && (
              <iframe
                title="PDF Preview"
                src={previewUrl}
                data-testid="pdf-preview-iframe"
                className="w-full h-full border-0"
              />
            )}
          </div>
          <DialogFooter className="px-6 py-3 border-t border-slate-200 gap-2">
            <Button variant="outline" onClick={closePreview} data-testid="close-preview-btn">Close</Button>
            <Button
              onClick={handleDownloadPdf}
              data-testid="download-from-preview-btn"
              className="bg-slate-900 hover:bg-slate-800"
            >
              <FilePdf size={18} weight="bold" className="mr-1" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Email Invoice</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEmailInvoice} className="space-y-4">
            <div>
              <Label>Recipient Email *</Label>
              <Input type="email" data-testid="email-to-input" value={emailForm.to_email} onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })} placeholder="customer@example.com" required />
            </div>
            <div>
              <Label>Subject</Label>
              <Input data-testid="email-subject-input" value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea data-testid="email-message-input" value={emailForm.message} onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} placeholder="Optional message to include" rows={3} />
            </div>
            {emailResult && (
              <div className={`p-3 rounded-sm text-sm ${emailResult.status === "error" ? "bg-red-50 text-red-700" : emailResult.status === "sent" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                {emailResult.message}
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="send-email-btn" disabled={emailSending} className="bg-green-600 hover:bg-green-700">{emailSending ? "Sending..." : "Send Email"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* E-Way Bill Dialog — free NIC methods (SMS + Bulk JSON) & manual EWB save */}
      <Dialog open={ewbDialogOpen} onOpenChange={setEwbDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>E-Way Bill — Free Generation (NIC Official)</DialogTitle>
          </DialogHeader>
          {ewbStatus && (
            <div className={`border rounded-sm p-3 text-xs mb-1 ${ewbStatus.required ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-slate-50 border-slate-200 text-slate-700"}`}>
              <b>Invoice total:</b> ₹{ewbStatus.total?.toFixed(2)} · <b>{ewbStatus.type === "intra" ? "Intra-state" : "Inter-state"}</b> · Threshold ₹{ewbStatus.threshold?.toLocaleString()} → EWB {ewbStatus.required ? <b>REQUIRED</b> : "optional"}
            </div>
          )}

          {/* Step 1: transport details */}
          <div className="border border-slate-200 rounded-sm p-4">
            <p className="text-sm font-bold text-slate-900 mb-3">1. Transport Details</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Vehicle Number</Label>
                <Input data-testid="ewb-vehicle" value={ewbForm.vehicle_number} onChange={(e) => setEwbForm({...ewbForm, vehicle_number: e.target.value.toUpperCase()})} placeholder="MH01AB1234" />
              </div>
              <div>
                <Label>Approx Distance (km) *</Label>
                <Input type="number" min="0" data-testid="ewb-distance" value={ewbForm.distance_km} onChange={(e) => setEwbForm({...ewbForm, distance_km: e.target.value})} />
              </div>
              <div>
                <Label>Transporter ID (optional)</Label>
                <Input data-testid="ewb-transporter-id" value={ewbForm.transporter_id} onChange={(e) => setEwbForm({...ewbForm, transporter_id: e.target.value})} placeholder="15-digit TRANSIN" />
              </div>
              <div>
                <Label>Mode</Label>
                <select data-testid="ewb-mode" value={ewbForm.mode_of_transport} onChange={(e) => setEwbForm({...ewbForm, mode_of_transport: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm">
                  <option value="1">Road</option>
                  <option value="2">Rail</option>
                  <option value="3">Air</option>
                  <option value="4">Ship</option>
                </select>
              </div>
            </div>
          </div>

          {/* Step 2: Auto-Generate via Master GSP (recommended — one click) */}
          <div className="border-2 border-blue-500 bg-blue-50 rounded-sm p-4">
            <p className="text-sm font-bold text-blue-900 mb-1">2. ⚡ Auto-Generate (via Platform GSP — recommended)</p>
            <p className="text-xs text-blue-800 mb-3">
              The EWB number is generated in one click. <b>₹1.00</b> is deducted from your wallet. Setup required: <b>Settings → E-Way Bill Portal API user</b>.
            </p>
            <Button type="button" onClick={handleAutoGenerateEwb} disabled={ewbAutoLoading}
              data-testid="ewb-auto-generate-btn"
              className="bg-blue-700 hover:bg-blue-800 text-white font-bold w-full sm:w-auto">
              {ewbAutoLoading ? "Generating via GSP…" : "⚡ Auto-Generate E-Way Bill (₹1.00)"}
            </Button>
            {!ewbStatus?.configured && (
              <p className="text-[11px] text-amber-700 mt-2">
                ⚠️ {ewbStatus?.platform_ready === false ? "The Master GSP has not been configured by the Super Admin yet." : "You have not added the EWB portal API user in Settings."}
              </p>
            )}
          </div>

          {/* Step 3: choose free method */}
          <div className="border border-slate-200 rounded-sm p-4">
            <p className="text-sm font-bold text-slate-900 mb-1">3. Free NIC Fallback (SMS / Bulk JSON)</p>
            <p className="text-xs text-slate-500 mb-3">If GSP is not set up, use these free NIC options.</p>
            <div className="flex gap-3 flex-wrap">
              <Button type="button" onClick={handleFetchSms} disabled={ewbSmsLoading} data-testid="ewb-sms-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {ewbSmsLoading ? "Preparing..." : "📱 SMS se Generate (1-Tap)"}
              </Button>
              <Button type="button" variant="outline" onClick={handleDownloadEwbJson} disabled={ewbJsonLoading} data-testid="ewb-json-btn" className="border-blue-300 text-blue-700 hover:bg-blue-50">
                {ewbJsonLoading ? "Building..." : "⬇️ Portal JSON Download"}
              </Button>
            </div>

            {ewbSms && (
              <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-sm p-3 space-y-2" data-testid="ewb-sms-box">
                {ewbSms.missing?.length > 0 && (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm p-2">
                    <b>Missing:</b> {ewbSms.missing.join(", ")} — fill in the blanks (____) yourself, or add the details and try again.
                  </div>
                )}
                <p className="text-xs text-emerald-900 font-semibold">Ye SMS <b>{ewbSms.sms_to}</b> par apne GST-registered mobile se bhejein:</p>
                <div className="bg-white border border-emerald-300 rounded-sm p-2 font-mono text-xs break-all" data-testid="ewb-sms-text">{ewbSms.sms_text}</div>
                <div className="flex gap-2 flex-wrap">
                  <Button type="button" size="sm" variant="outline" onClick={handleCopySms} data-testid="ewb-sms-copy-btn">
                    {ewbSmsCopied ? "✓ Copied!" : "Copy SMS"}
                  </Button>
                  <a href={`sms:${ewbSms.sms_to}?&body=${encodeURIComponent(ewbSms.sms_text)}`} data-testid="ewb-sms-open-link"
                     className="inline-flex items-center px-3 py-1.5 bg-emerald-600 text-white rounded-sm text-xs font-semibold hover:bg-emerald-700">
                    Open SMS App →
                  </a>
                </div>
                <p className="text-[11px] text-emerald-800">{ewbSms.note} NIC will reply via SMS with the 12-digit EWB number — save it in Step 3 below.</p>
              </div>
            )}

            <p className="text-[11px] text-slate-500 mt-3">
              <b>JSON option:</b> After downloading, log in at <a href="https://ewaybillgst.gov.in" target="_blank" rel="noreferrer" className="text-blue-600 underline">ewaybillgst.gov.in</a> and upload under <b>e-Waybill → Generate Bulk</b> — all EWBs are generated instantly.
            </p>
          </div>

          {/* Step 3: save EWB number */}
          <div className="border border-slate-200 rounded-sm p-4">
            <p className="text-sm font-bold text-slate-900 mb-3">3. Save the Received EWB Number</p>
            <div className="flex gap-2">
              <Input data-testid="ewb-number-input" value={ewbNumberInput} onChange={(e) => setEwbNumberInput(e.target.value)} placeholder="12-digit EWB number (e.g. 391002345678)" />
              <Button type="button" onClick={handleSaveEwbNumber} disabled={ewbSaving || !ewbNumberInput.trim()} data-testid="ewb-save-btn" className="bg-slate-900 hover:bg-slate-800 text-white whitespace-nowrap">
                {ewbSaving ? "Saving..." : "Save EWB"}
              </Button>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">Once saved, the EWB number automatically appears on the invoice PDF and in records.</p>
          </div>

          {ewbError && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 rounded-sm" data-testid="ewb-error-msg">{ewbError}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEwbDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
